import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { QueryKey } from '@tanstack/react-query'
import { eventsApi } from '@/api/endpoints'
import type { Event } from '@/types'

// #122 — toggle an event's favourite state with an optimistic update across every
// cached events feed (all-events + favourites), rollback on error, and a
// favourites-feed invalidation so an un-favourited event drops out on reconcile.

interface EventPage {
  results: Event[]
  [k: string]: unknown
}
interface InfiniteEvents {
  pages: EventPage[]
  [k: string]: unknown
}

function isInfiniteEvents(v: unknown): v is InfiniteEvents {
  return !!v && typeof v === 'object' && Array.isArray((v as { pages?: unknown }).pages)
}

export function useToggleFavorite(eventId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (next: boolean) => eventsApi.setFavorite(eventId, next),
    onMutate: async (next: boolean) => {
      await queryClient.cancelQueries({ queryKey: ['events'] })
      const prev = queryClient.getQueriesData({ queryKey: ['events'] })
      queryClient.setQueriesData({ queryKey: ['events'] }, (old: unknown) => {
        if (!isInfiniteEvents(old)) return old
        return {
          ...old,
          pages: old.pages.map((p) => ({
            ...p,
            results: p.results.map((e) =>
              e.id === eventId ? { ...e, is_favorite: next } : e),
          })),
        }
      })
      return { prev }
    },
    onError: (_err, _next, ctx) => {
      ctx?.prev?.forEach(([key, data]: [QueryKey, unknown]) =>
        queryClient.setQueryData(key, data))
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['events', 'favourites'] })
    },
  })
}
